/* eslint-disable no-unused-vars */
import { formatDate } from '../utils/formatDate';
import { translateWorkShift } from '../utils/translateWorkShift';

/**
 * Verifica se existem eventos de agendamento conflitantes (mesmo dia e mesmo
 * turno) entre diferentes profissionais.
 *
 * @param {Array<Object>} schedulingEvents - Array de objetos de agendamento.
 * Cada objeto deve ter:
 * - professionalName (string)
 * - periodsForVisit (Array<string>) - array de strings de data/hora (ex: '2025-10-01T14:00:00')
 * - formVisits.workShift (string) - 'manhã', 'tarde', 'noite', etc.
 * @returns {Array<string>} - Array de mensagens de erro para agendamentos conflitantes.
 */
function check(schedulingEvents) {
  // ===================================================================
  // 1. Estrutura para agrupar profissionais por 'Data_Formatada|Turno'
  // ===================================================================
  const conflictsMap = new Map();

  // =========================
  // 2. Normalizar e Agrupar
  // =========================
  for (const schedulingEvent of schedulingEvents) {
    const { code, professionalName, periodsForVisit, formVisits } = schedulingEvent;
    const { workShift } = formVisits;

    for (const periodVisit of periodsForVisit) {
      const { start: dateISOString } = periodVisit;
      const formattedDate = formatDate(dateISOString);
      // Cria uma chave única de agrupamento
      const key = `${formattedDate}|${workShift}`;

      if (!conflictsMap.has(key)) {
        conflictsMap.set(key, {
          date: formattedDate,
          shift: workShift,
          professionals: new Set()
        });
      }

      // Adiciona o nome do profissional ao conjunto de profissionais para essa chave
      conflictsMap.get(key).professionals.add(professionalName);
    }
  }

  const detailedErrors = [];
  const summaryErrors = [];

  // ============================
  // 3. Filtrar e Formatar Erros
  // ============================
  for (const group of conflictsMap.values()) {
    // Se o número de profissionais for maior que 1, temos um conflito
    if (group.professionals.size > 1) {
      // Converte o Set de nomes para um Array e formata para a mensagem de erro
      const professionalsList = Array.from(group.professionals);

      let namesString;
      if (professionalsList.length === 2) {
        namesString = `${professionalsList[0]} e ${professionalsList[1]}`;
      } else {
        const last = professionalsList.pop();
        namesString = `${professionalsList.join(', ')} e ${last}`;
      }

      const periodStr = translateWorkShift(group.shift);

      // Formata a string de erro como no exemplo
      const errorMessage = `Os profissionais ${namesString} possuem visitas para o dia ${group.date} para o período da ${periodStr}`;

      const aggregateErrorsMsg = `Os profissionais ${namesString} possuem visitas para o(s) mesmo(s) dia(s) no mesmo período (${periodStr}).`;
      if (!summaryErrors.find(err => err === aggregateErrorsMsg)) {
        summaryErrors.push(aggregateErrorsMsg);
      }

      detailedErrors.push(errorMessage);
    }
  }

  return { detailedErrors, summaryErrors };
}

export { check };
